import style from "./Smist.module.css";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import z from "zod";
import {
  Alert,
  Autocomplete,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { FormLabel } from "../../components/form-label/FormLabel";
import {
  endpoint,
  provincieItaliane,
  required_error,
} from "../../services/shared";
import { Rules } from "../../types";
import ListaRegole from "../../components/lista-regole/ListaRegole";
import Snackbar from "@mui/material/Snackbar";
import {apiClient} from "../../services/api";

type FormData = z.infer<typeof formSchema>;
const formSchema = z.object({
  uid: z.string().min(1).optional(),
  name: z.string({ required_error }).min(3, "Deve avere almeno 3 caratteri"),
  province_preferences: z.array(z.string()),
  cap_month: z
    .number({ required_error, invalid_type_error: "Formato non valido" })
    .int()
    .positive("Solo numeri positivi"),
  campaign: z
    .number({ required_error, invalid_type_error: "Formato non valido" })
    .int()
    .positive("Solo numeri positivi")
    .max(9999),
  distribution_weight: z
    .number({ required_error, invalid_type_error: "Formato non valido" })
    .min(0)
    .max(1),
  active: z.boolean({ required_error }),
  accept_out_preference: z.boolean({ required_error }),
  distribution_channel: z.enum(["hub", "rete"], {
    errorMap: () => ({ message: "Seleziona destinazione" }),
  }),
});
export default function Smist() {
  const [regolaSelezionata, setRegolaSelezionata] = useState<Rules>();
  const [snackState, setSnackState] = useState<boolean>(false);
  const [snackMessage, setSnackMessage] = useState<{
    severity: any;
    message: string;
  }>({
    severity: "success",
    message: "",
  });
  const defaultValues = {
    name: "",
    province_preferences: [],
    cap_month: undefined,
    campaign: undefined,
    distribution_weight: undefined,
    active: undefined,
    accept_out_preference: undefined,
    distribution_channel: undefined,
  };
  const [method, setMethod] = useState<"create" | "update">("create");
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = form;
  const onSubmit = handleSubmit(
    async (data) => {
      setSnackState(false);
      let res = null;
      const payload =
        method === "update" ? { ...data, uid: regolaSelezionata?.uid } : data;
      try {
        res = await apiClient.post(method === "create" ?
            `/v1/rule/${method}` :
            `/v1/rule/${method}/${payload["uid"]}`, payload, {
            headers: { "Content-Type": "application/json" },
        });
      } catch (e) {
        console.error("errore", e);
        setSnackMessage({
          severity: "error",
          message: "Qualsosa è andato storto",
        });
        setSnackState(true);
      }
      if (res?.status && res?.status === 200) {
        setSnackMessage({ severity: "success", message: res?.statusText });
        setSnackState(true);
        setMethod("create");
        setRegolaSelezionata(undefined);
        reset(defaultValues);
      }
    },
    (formErrors) => {
      console.log("❌ Validation errors:", formErrors);
    },
  );
  // ascolto le variazioni del method
  useEffect(() => {
    if (method === "update" && regolaSelezionata) {
      reset(regolaSelezionata);
    } else {
      reset(defaultValues);
    }
  }, [method, regolaSelezionata, reset]);
  const ruleHandleSelection = (rule: Rules) => {
    setRegolaSelezionata(rule);
    setMethod("update");
  };
  return (
    <div className={"container"}>
      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackState}
        onClose={() => setSnackState(false)}
        autoHideDuration={3000}
      >
        <Alert
          onClose={() => setSnackState(false)}
          severity={snackMessage.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackMessage.message}
        </Alert>
      </Snackbar>
      <div className={"w100 flex-col"}>
        <form
          onSubmit={onSubmit}
          style={{
            border: "1px solid #dedede",
            borderRadius: ".5em",
            padding: "0em 1em",
            paddingBottom: "1.5em",
            marginBottom: "1em",
            marginTop: "1em",
            maxWidth: "100%",
            background: "white",
          }}
          className="flex-col gap-2"
        >
          <FormLabel label={"Crea regole di dispatching"}></FormLabel>
          <div className={style.fieldContainer}>
            <Controller
              name={"name"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  label="Nome regola"
                  placeholder="Digita nome"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                />
              )}
            />
            <Controller
              name={"distribution_channel"}
              control={control}
              render={({ field }) => (
                <div style={{ width: "100%", maxWidth: "195px" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="distribution_channel">
                      Canale destinazione
                    </InputLabel>
                    <Select
                      {...field}
                      style={{ textAlign: "left" }}
                      labelId="distribution_channel"
                      value={field.value || ""}
                      error={!!errors.distribution_channel}
                    >
                      <MenuItem value="" disabled>
                        Seleziona una opzione
                      </MenuItem>
                      <MenuItem value="hub">HUB</MenuItem>
                      <MenuItem value="rete">Rete</MenuItem>
                    </Select>
                    {errors.distribution_channel && (
                      <FormHelperText style={{ color: "#d32f2f" }}>
                        {errors.distribution_channel.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            />
            <Controller
              name={"province_preferences"}
              control={control}
              render={({ field }) => (
                <Autocomplete
                  size="small"
                  disablePortal
                  multiple={true}
                  options={provincieItaliane}
                  sx={{ width: 300 }}
                  value={field.value || []}
                  onChange={(_, newValue) => field.onChange(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Provincie prioritarie"
                      error={!!errors.province_preferences}
                      helperText={errors.province_preferences?.message}
                    />
                  )}
                />
              )}
            />
            <Controller
              name="accept_out_preference"
              control={control}
              render={({ field }) => (
                <div style={{ width: "100%", maxWidth: "195px" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="accept_out_preference">
                      Provincie non prioritarie
                    </InputLabel>
                    <Select
                      {...field}
                      style={{ textAlign: "left" }}
                      labelId="accept_out_preference"
                      value={
                        typeof field.value === "boolean" ? field.value : ""
                      }
                      onChange={(e) =>
                        field.onChange(e.target.value === "true")
                      }
                      error={!!errors.accept_out_preference}
                    >
                      <MenuItem value="" disabled>
                        Seleziona una opzione
                      </MenuItem>
                      <MenuItem value="true">Accettate</MenuItem>
                      <MenuItem value="false">Respinte</MenuItem>
                    </Select>
                    {errors.active && (
                      <FormHelperText style={{ color: "#d32f2f" }}>
                        {errors.active.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            />
            <Controller
              name={"cap_month"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  value={field.value ?? ""}
                  type={"number"}
                  size="small"
                  label="Cap mese"
                  placeholder="Es. 100"
                  error={!!errors.cap_month}
                  helperText={errors.cap_month?.message}
                />
              )}
            />
            <Controller
              name={"distribution_weight"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  value={field.value ?? ""}
                  type={"number"}
                  size="small"
                  label="% distribuzione"
                  placeholder="Es. 0,3"
                  error={!!errors.distribution_weight}
                  helperText={
                    errors.distribution_weight?.message
                      ? errors.distribution_weight?.message
                      : "Valore da 0 a 1"
                  }
                />
              )}
            />
            <Controller
              name={"campaign"}
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  value={field.value ?? ""}
                  type={"number"}
                  size="small"
                  label="Campagna"
                  placeholder="Es. 1073"
                  error={!!errors.campaign}
                  helperText={errors.campaign?.message}
                />
              )}
            />
            <Controller
              name="active"
              control={control}
              render={({ field }) => (
                <div style={{ width: "100%", maxWidth: "195px" }}>
                  <FormControl fullWidth size="small">
                    <InputLabel id="active">Stato regola</InputLabel>
                    <Select
                      {...field}
                      style={{ textAlign: "left" }}
                      labelId="active"
                      value={
                        typeof field.value === "boolean" ? field.value : ""
                      }
                      onChange={(e) =>
                        field.onChange(e.target.value === "true")
                      }
                      error={!!errors.active}
                    >
                      <MenuItem value="" disabled>
                        Seleziona una opzione
                      </MenuItem>
                      <MenuItem value="true">Attiva</MenuItem>
                      <MenuItem value="false">Non attiva</MenuItem>
                    </Select>
                    {errors.active && (
                      <FormHelperText style={{ color: "#d32f2f" }}>
                        {errors.active.message}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
              )}
            />
          </div>
          <div className={"flex-row gap-2"}>
            <Button
              style={{ minWidth: "200px" }}
              variant="contained"
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting
                ? "Elaboro richiesta..."
                : method === "update"
                  ? "Salva"
                  : "Aggiungi"}
            </Button>
            {method === "update" && (
              <Button
                onClick={() => setMethod("create")}
                variant="outlined"
                type="button"
                disabled={isSubmitting}
              >
                Annulla modifiche
              </Button>
            )}
          </div>
        </form>
        <ListaRegole onSelect={ruleHandleSelection} />
      </div>
    </div>
  );
}
